import { useState, useEffect } from 'react';
import { ServiceImagePlaceholder } from '@/components/shared/ServiceImagePlaceholder';

interface ImageLoaderProps {
  imageId: string;
  imageUrl: string;
  altText: string;
  isLoaded: boolean;
  onLoad: (id: string) => void;
  showPlaceholder?: boolean;
}

export const ImageLoader = ({
  imageId,
  imageUrl,
  altText,
  isLoaded,
  onLoad,
  showPlaceholder = true
}: ImageLoaderProps) => {
  const [localLoaded, setLocalLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  
  // Check if the image URL is empty or invalid
  const isEmptyOrInvalidUrl = !imageUrl || imageUrl.trim() === '';
  
  // Set a timeout to ensure we don't get stuck in loading state forever
  useEffect(() => {
    // Don't set timeout if image URL is empty - just mark as "loaded"
    if (isEmptyOrInvalidUrl) {
      onLoad(imageId);
      setIsLoading(false);
      return;
    }
    
    const timeout = setTimeout(() => {
      if (isLoading) {
        console.log('Image load timeout triggered for:', imageId);
        onLoad(imageId);
        setIsLoading(false);
      }
    }, 10000); // Increased timeout to 10 seconds
    
    return () => clearTimeout(timeout);
  }, [imageId, isLoading, onLoad, isEmptyOrInvalidUrl]);
  
  const handleImageLoad = () => {
    console.log('Image loaded in ImageLoader component:', imageId);
    setLocalLoaded(true);
    setIsLoading(false);
    setHasError(false);
    onLoad(imageId);
  };
  
  // If the image fails to load
  const handleImageError = () => {
    console.log('Image failed to load:', imageId);
    setHasError(true);
    setIsLoading(false);
    
    // Retry loading the image if we haven't exceeded max retries
    if (retryCount < MAX_RETRIES) {
      console.log(`Retrying image load (attempt ${retryCount + 1}/${MAX_RETRIES}):`, imageId);
      setRetryCount(prev => prev + 1);
      setIsLoading(true);
      setHasError(false);
    } else {
      console.log('Max retries exceeded for image:', imageId);
      onLoad(imageId); // Mark as loaded to remove spinner
    }
  };

  // If the image URL is empty or loading failed after all retries, show placeholder
  if (isEmptyOrInvalidUrl || (hasError && retryCount >= MAX_RETRIES)) {
    return <ServiceImagePlaceholder serviceName={altText} />;
  }

  return (
    <>
      <div className={`w-full h-full bg-gray-50 absolute ${!isLoading ? 'hidden' : 'flex items-center justify-center'}`}>
        <div className="w-8 h-8 border-4 border-gebeya-pink border-t-transparent rounded-full animate-spin"></div>
      </div>
      <img
        src={imageUrl}
        alt={altText}
        className={`w-full h-full object-cover object-center transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        onLoad={handleImageLoad}
        onError={handleImageError}
        loading="lazy"
        decoding="async"
        key={`${imageId}-${retryCount}`} // Force image reload on retry
        style={{ aspectRatio: '1/1' }}
      />
    </>
  );
};
